/**
 * A fader's audio taper (GH #2897, GH #3001). Loudness follows dB, and the travel follows console practice: fine over
 * the top three-quarters, where the balancing happens, and a quicker fall to silence over the bottom quarter.
 *
 * - From 25% to the top, each 1% of travel is 0.4 dB: the top is 0 dB (gain 1), 75% is −10 dB, half way is −20 dB and
 *   25% is −30 dB.
 * - Below 25%, each 1% is 1.2 dB, down to −60 dB just above the bottom. The bottom is silence.
 *
 * Gain is what is stored and applied; only the fader's position goes through the taper, so existing mixes sound the
 * same.
 */

/** The dB span of the travel above silence: just above the bottom is −60 dB */
export const faderRangeDB = 60

/** Where the two segments meet, and the gain there */
const kneePosition = 0.25
const kneeDB = -30
/** dB per unit of travel above and below the knee */
const upperSlope = -kneeDB / (1 - kneePosition)
const lowerSlope = (faderRangeDB + kneeDB) / kneePosition

const clamp = (value: number) => Math.min(Math.max(value, 0), 1)

const positionToDB = (p: number) => (p >= kneePosition ? upperSlope * (p - 1) : -faderRangeDB + lowerSlope * p)

const dbToPosition = (db: number) => (db >= kneeDB ? 1 + db / upperSlope : (db + faderRangeDB) / lowerSlope)

/** The linear gain for a fader position in [0, 1] */
export const positionToGain = (position: number) => {
  const p = clamp(position)
  return p === 0 ? 0 : 10 ** (positionToDB(p) / 20)
}

/** The fader position in [0, 1] for a linear gain; below the range sits at the bottom, above 0 dB at the top */
export const gainToPosition = (gain: number) => (gain > 0 ? clamp(dbToPosition(20 * Math.log10(gain))) : 0)

/** The gain applied, in dB; silence is −Infinity */
export const gainToDB = (gain: number) => (gain > 0 ? 20 * Math.log10(gain) : -Infinity)

/** What a screen reader announces for a gain: "-6.0 dB", or "silent" at the bottom */
export const gainAriaValueText = (gain: number) => (gain > 0 ? `${gainToDB(gain).toFixed(1)} dB` : 'silent')
